/**
 * ttrGraph.ts — 티켓 투 라이드 클라이언트 그래프 헬퍼 (UX 한정 시인성용).
 *
 * 권위는 서버 (BoardServer/Common/TtrGraph.cs / sp_TtrEndGame). 본 클라이언트 모듈은
 * 본인 핸드 패널의 티켓 완료 여부 실시간 표시(UX 시인성)에만 사용된다.
 *
 * SoT: H:\AI\AI_Setting\보드겜돌이\티켓투라이드.md
 *   §3-3 (컴포넌트 명세 — util/ttrGraph.ts BFS 티켓 완료 판정)
 *   §3-1 (본인 티켓: 도시 쌍 + 점수 + 완료 상태 — **본인 클라 한정 실시간 BFS 시인성**)
 *
 * Phase 0 — 자료구조 + 시그니처 + 단순 BFS 구현 1종(IsConnected).
 *   CalcAllTicketsCompletion 등 일괄 함수는 IsConnected를 조합해 호출자가 처리.
 *   서버 권위 계산과 동률 보장은 게임 종료 시 broadcast(`TtrGameEnded.scoreBreakdown`)가 무효화.
 */

// ── 자료구조 ─────────────────────────────────────────────

/** 점유된 노선 한 lane (UX 그래프 입력). 서버 TtrRouteClaimed broadcast → ticketStore 누적 결과 */
export interface ClaimedEdgeUx {
  routeId:        number
  laneNo:         number
  cityAId:        number
  cityBId:        number
  length:         number
  ownerUserCode:  number
}

/** 본인 티켓 1건 UX 표시용 결과 */
export interface TicketCompletionUx {
  ticketId:   number
  cityAId:    number
  cityBId:    number
  points:     number
  /** 본인 점유 노선만으로 두 도시 연결 여부 */
  isComplete: boolean
}

/** 인접 리스트 — key: cityId, value: 이웃 도시 + 노선 metadata */
export type Adjacency = Map<number, Array<{
  other:   number
  length:  number
  routeId: number
  laneNo:  number
}>>

// ── BuildPlayerAdjacency ────────────────────────────────
/**
 * 단일 플레이어 점유 노선만으로 양방향 인접 리스트 구축.
 * UX 사이드 — 본인 user_code 필터링 후 호출.
 */
export function buildPlayerAdjacency(edges: ClaimedEdgeUx[]): Adjacency {
  const adj: Adjacency = new Map()
  for (const e of edges) {
    if (!adj.has(e.cityAId)) adj.set(e.cityAId, [])
    if (!adj.has(e.cityBId)) adj.set(e.cityBId, [])
    adj.get(e.cityAId)!.push({ other: e.cityBId, length: e.length, routeId: e.routeId, laneNo: e.laneNo })
    adj.get(e.cityBId)!.push({ other: e.cityAId, length: e.length, routeId: e.routeId, laneNo: e.laneNo })
  }
  return adj
}

// ── IsConnected ─────────────────────────────────────────
/**
 * BFS 로 두 도시가 연결되어 있는지 판정. 동일 도시는 즉시 true.
 * 도시 ID 가 인접 리스트에 미존재(점유 노선이 도시에 닿지 않음)면 false.
 */
export function isConnected(adj: Adjacency, srcCityId: number, dstCityId: number): boolean {
  if (srcCityId === dstCityId) return true
  if (!adj.has(srcCityId) || !adj.has(dstCityId)) return false

  const visited = new Set<number>([srcCityId])
  const queue:   number[] = [srcCityId]

  while (queue.length > 0) {
    const cur = queue.shift()!
    const neighbors = adj.get(cur)
    if (!neighbors) continue
    for (const n of neighbors) {
      if (n.other === dstCityId) return true
      if (!visited.has(n.other)) {
        visited.add(n.other)
        queue.push(n.other)
      }
    }
  }
  return false
}

// ── CalcMyTicketsCompletion ─────────────────────────────
/**
 * 본인 보유 티켓 일괄 완료 판정 (UX 핸드 패널 렌더용).
 * 권위 점수 계산은 본 함수가 처리하지 않음 — 게임 종료 시 서버 broadcast 가 최종.
 */
export function calcMyTicketsCompletion(
  myEdges:   ClaimedEdgeUx[],
  myTickets: Array<{ ticketId: number; cityAId: number; cityBId: number; points: number }>,
): TicketCompletionUx[] {
  const adj = buildPlayerAdjacency(myEdges)
  return myTickets.map(t => ({
    ticketId:   t.ticketId,
    cityAId:    t.cityAId,
    cityBId:    t.cityBId,
    points:     t.points,
    isComplete: isConnected(adj, t.cityAId, t.cityBId),
  }))
}

// ── CalcLongestPath (보드 ring 시인성, SEQ=10186 fine-tune ①) ─────
/**
 * 단일 플레이어 점유 노선만으로 최장 경로(edge 합산 길이) + 경로 lane 키 목록 계산.
 *
 * 규칙(USA 원작 룰):
 *   - edge(lane 단위) 1회만 사용 (같은 ROUTE_ID/LANE_NO 재방문 금지)
 *   - 같은 도시 재방문 허용 (cycle 허용)
 *   - 시작 도시 자유 (모든 점유 노선 양끝점 starting point 시도)
 *
 * 권위는 서버 sp_TtrCalcLongestPath. 본 함수는 보드 ring 시인성용.
 * USA 보드 한 플레이어 점유 보통 N≤20, cap=22 안전장치 (N>22 시 greedy fallback).
 *
 * @return { length: 총 길이, laneKeys: Set<`${routeId}:${laneNo}`> }
 *         사용된 edge 가 없으면 length=0, laneKeys=빈 Set.
 */
const LONGEST_PATH_DFS_CAP = 22

export interface LongestPathResultUx {
  length:   number
  laneKeys: Set<string>
}

export function calcLongestPath(
  routeClaims: Array<{ routeId: number; laneNo: number; ownerUserCode: number }>,
  routesMaster: Array<{ routeId: number; cityAId: number; cityBId: number; length: number; laneNo: number }>,
  userCode: number,
): LongestPathResultUx {
  // 본인 edges 만 필터 + 마스터 join 으로 length/city 채우기
  const myEdges: Array<{ routeId: number; laneNo: number; cityAId: number; cityBId: number; length: number }> = []
  for (const c of routeClaims) {
    if (c.ownerUserCode !== userCode) continue
    const m = routesMaster.find(r => r.routeId === c.routeId && r.laneNo === c.laneNo)
    if (!m) continue
    myEdges.push({
      routeId: c.routeId,
      laneNo:  c.laneNo,
      cityAId: m.cityAId,
      cityBId: m.cityBId,
      length:  m.length,
    })
  }
  if (myEdges.length === 0) return { length: 0, laneKeys: new Set<string>() }

  // 인접 리스트 — city → (other, edgeKey, length)
  const adj = new Map<number, Array<{ other: number; key: string; length: number }>>()
  for (const e of myEdges) {
    const key = `${e.routeId}:${e.laneNo}`
    if (!adj.has(e.cityAId)) adj.set(e.cityAId, [])
    if (!adj.has(e.cityBId)) adj.set(e.cityBId, [])
    adj.get(e.cityAId)!.push({ other: e.cityBId, key, length: e.length })
    adj.get(e.cityBId)!.push({ other: e.cityAId, key, length: e.length })
  }

  // N > cap → greedy fallback (단순 longest single edge 시작 그리디 — 정확성 양보, 보드 ring 시인성 보장)
  if (myEdges.length > LONGEST_PATH_DFS_CAP) {
    let bestLen = 0
    let bestKeys = new Set<string>()
    // 가장 긴 edge 1개 = 보드 시인성 fallback
    for (const e of myEdges) {
      if (e.length > bestLen) {
        bestLen = e.length
        bestKeys = new Set([`${e.routeId}:${e.laneNo}`])
      }
    }
    return { length: bestLen, laneKeys: bestKeys }
  }

  let bestLength = 0
  let bestLanes: Set<string> = new Set<string>()

  // 시작 city = 인접 리스트 전체 (cycle 허용이라 모든 endpoint 시도)
  const usedKeys = new Set<string>()
  const trailKeys: string[] = []

  function dfs(cur: number, accLen: number): void {
    if (accLen > bestLength) {
      bestLength = accLen
      bestLanes = new Set(trailKeys)
    }
    const neighbors = adj.get(cur)
    if (!neighbors) return
    for (const n of neighbors) {
      if (usedKeys.has(n.key)) continue
      usedKeys.add(n.key)
      trailKeys.push(n.key)
      dfs(n.other, accLen + n.length)
      trailKeys.pop()
      usedKeys.delete(n.key)
    }
  }

  const startCities: number[] = []
  adj.forEach((_v, k) => startCities.push(k))
  for (let i = 0; i < startCities.length; i++) {
    dfs(startCities[i], 0)
  }

  return { length: bestLength, laneKeys: bestLanes }
}
